/**
 * Tick-list templates — ported verbatim from v8 (Quarters HS).
 * Item content is HSE / CDM compliance text and should not be casually edited.
 */

export type TickKind = 'daily' | 'weekly' | 'monthly' | 'telehandler_weekly';

export interface TickItemSpec {
  text: string;
  desc?: string;       // optional "how to check" guidance (used by telehandler)
}

export interface TickSectionSpec {
  name?: string;       // optional section heading; flat lists have one nameless section
  items: TickItemSpec[];
}

export interface TickListTemplate {
  kind: TickKind;
  title: string;
  shortTitle: string;
  subtitle: string;
  sections: TickSectionSpec[];
  tail?: string;       // optional footer block (e.g. "If you can't tick…")
}

const s = (text: string): TickItemSpec => ({ text });

export const TICK_LISTS: Record<TickKind, TickListTemplate> = {
  daily: {
    kind: 'daily',
    title: 'Daily Site Setup Check',
    shortTitle: 'Daily Site Setup',
    subtitle: '30 seconds. Every morning. Site Manager.',
    sections: [{
      items: [
        s('Gate / hoarding secure overnight — no break-in, no public access overnight'),
        s('Welfare working — water, kettle, WC flushing, hand-wash, kitchen clean'),
        s('First aid kit + eyewash present, in date'),
        s('Fire extinguisher in cabin / hot-works area, gauge in green'),
        s('Site signs all up (PPE, emergency, sign-in, fire action)'),
        s('Scaffold tag in date (this week\'s inspection)'),
        s('Excavations — barriers up, no water, edges stable'),
        s('MEWP / telehandler — pre-use check done & logged'),
        s('Temporary electrics — RCD working, no damaged leads'),
        s('Housekeeping — yesterday\'s mess cleared, materials stacked safely'),
        s('Anyone on site I haven\'t inducted? Stop them; induct first'),
        s('Today\'s RAMS / permits briefed to relevant people'),
      ],
    }],
    tail: 'If you can\'t tick something, you don\'t open the gates until it is fixed.',
  },

  weekly: {
    kind: 'weekly',
    title: 'Weekly Site Walk',
    shortTitle: 'Weekly Walk',
    subtitle: '5–10 minutes. Once a week. Photo every defect.',
    sections: [
      {
        name: '1. Site set-up & welfare',
        items: [
          s('Hoarding / fencing intact, no public access points'),
          s('Welfare clean, stocked (soap, towels, drinking water, kettle)'),
          s('Site signs visible, weather-proof, accurate'),
          s('Emergency contacts displayed in cabin and current'),
          s('Skip / waste segregated, not overfilled, no fly-tipping'),
        ],
      },
      {
        name: '2. Working at height',
        items: [
          s('Scaffold tag updated this week. Toe-boards, guard-rails, mid-rails all present'),
          s('Ladders — only used for short-duration access, footed, secured top'),
          s('Edge protection on stairwells / openings / roof edges'),
          s('MEWP — daily check sheet up to date; harnesses on operator'),
          s('Mobile towers — assembled by PASMA-trained, outriggers down'),
        ],
      },
      {
        name: '3. Excavations & ground works',
        items: [
          s('Excavations — barriers, lighting, no water, sides supported >1.2m'),
          s('Permit-to-Dig issued for any new dig; CAT-scan done'),
          s('Spoil heaps not loading the edge of dig'),
          s('Inspections — daily by competent person, weekly recorded'),
        ],
      },
      {
        name: '4. PPE & people',
        items: [
          s('Everyone wearing baseline PPE (hat / boots / hi-vis)'),
          s('Task-specific PPE — gloves, glasses, masks (FFP3 for silica)'),
          s('All on-site personnel inducted and on sign-in sheet'),
          s('Subbie insurance / RAMS / cards on file for everyone here'),
        ],
      },
      {
        name: '5. Plant, tools, electrics',
        items: [
          s('Power leads — no damaged insulation, RCD in line, 110V on site'),
          s('Lifting accessories (slings, chains) — within LOLER date'),
          s('PAT stickers on company tools, all in date'),
        ],
      },
      {
        name: '6. Hot works, dust, COSHH',
        items: [
          s('Hot works — Permit displayed; fire watch + extinguisher on stand-by'),
          s('Cutting concrete / stone — water suppression OR on-tool extraction. FFP3'),
          s('COSHH register up to date for new products on site'),
          s('Spill kit available; bunds under fuel/oil drums'),
        ],
      },
      {
        name: '7. Asbestos / unknowns',
        items: [
          s('No suspect ACMs being disturbed. Survey on file for refurb'),
          s('Anyone uncovers something unexpected — STOP, photo, call WY'),
        ],
      },
    ],
  },

  monthly: {
    kind: 'monthly',
    title: 'Monthly H&S Review',
    shortTitle: 'Monthly H&S Review',
    subtitle: '15 minutes. End of every month. Director.',
    sections: [{
      items: [
        s('All active projects have a written CPP (Construction Phase Plan) up to date'),
        s('F10 (HSE project notification) displayed where notifiable'),
        s('Subcontractor insurance — none expiring next 30 days'),
        s('Plant Register — LOLER / PAT / service all in date'),
        s('COSHH register reflects materials currently on sites'),
        s('Site Folder physical copies match digital (last spot-check?)'),
        s('Toolbox talks delivered this month: target ≥1 per active site'),
        s('All weekly walks done (number expected vs done)'),
        s('Open actions from prior months — closed or carrying'),
        s('Public Liability, Employers Liability, and Professional Indemnity insurance all in date'),
        s('H&S Policy reviewed in last 12 months'),
      ],
    }],
  },

  telehandler_weekly: {
    kind: 'telehandler_weekly',
    title: 'Telehandler Weekly Check',
    shortTitle: 'Telehandler weekly',
    subtitle: '10 minutes. Once a week. Operator. Records support LOLER thorough exam between formal inspections.',
    sections: [{
      items: [
        { text: 'Tyres — pressure, condition, tread', desc: 'Walk round all wheels. Pressures match the plate (usually inside cab door). No deep cuts, splits or sidewall bulges. Tread depth ≥ 1.6mm. Note any uneven wear — could mean axle issue.' },
        { text: 'Wheels & nuts', desc: 'All wheel nuts present and tight. No cracks in the rim. If a nut\'s missing — stop, get it replaced before any lift.' },
        { text: 'Hydraulic system — leaks & oil', desc: 'Walk under the boom and round the rams. Look for fresh oil drips, wet patches, or oily soil. Check sight glass on hydraulic tank — oil at correct level when boom retracted.' },
        { text: 'Boom, chassis, welds', desc: 'Look for cracks, bent metal, paint flaking off welds (sign of crack underneath). Pay attention to pivot points and the boom telescoping section. Anything visible — report and stop using.' },
        { text: 'Stabilisers / outriggers (if fitted)', desc: 'Extend and retract each one. Should move smoothly without juddering. No leaks at base. Pads not bent or split.' },
        { text: 'Forks — wear, cracks, locking pins', desc: 'Fork heel wear should be no more than 10% of original thickness (use a wear gauge if you have one). No visible cracks. Locking pins drop in fully and lock the forks against shifting sideways.' },
        { text: 'Quick-hitch / attachment locking', desc: 'When attaching forks or any accessory: confirm the lock fully engages and the visible pin clicks home. Try to lift the attachment off without unlocking — should not come free.' },
        { text: 'Steering & 4-wheel modes', desc: 'Full lock left and right. Try all steer modes (2WS, 4WS, crab if fitted). No grinding or excessive play. If wheels don\'t centre — report.' },
        { text: 'Brakes — service & parking', desc: 'Service brake: firm pedal, machine stops promptly on flat ground. Parking brake: park on a slope, knock into neutral, machine should hold without rolling.' },
        { text: 'Lights — front, rear, beacon, indicators, work lights', desc: 'Every light works. Beacon (rotating amber) running whenever the engine is. Reverse alarm sounds when reversing. If anything\'s out — replace before next use.' },
        { text: 'Horn & reversing alarm', desc: 'Horn audible across the site. Reverse alarm clearly heard when reversing. Both legally required.' },
        { text: 'Mirrors & camera', desc: 'All mirrors clean, intact and adjusted for the operator. Reversing camera (if fitted) — clean lens, screen working in cab.' },
        { text: 'Cab — seatbelt, controls, glass', desc: 'Seatbelt latches and retracts properly. Controls labelled, no damage. No cracks in windscreen that affect view. Door closes and latches.' },
        { text: 'Engine bay — oil, coolant, fuel', desc: 'Engine oil dipstick: between min and max with engine cold. Coolant expansion tank: between marks. Fuel: enough for the day; sediment bowl clear of water.' },
        { text: 'Battery & electrics', desc: 'Battery terminals tight and clean. No corrosion. No exposed wires. Battery secured.' },
        { text: 'Decals — load chart & plate', desc: 'Load/lift chart visible inside cab. Capacity plate readable. CE/UKCA plate present. If they\'re scratched off — replace via the dealer.' },
        { text: 'Operator manual in cab', desc: 'Must be present in the cab — required by HSE and a defence if anything goes wrong.' },
        { text: 'LOLER thorough exam date', desc: 'Check the LOLER report date — every 12 months for general lifting; every 6 months if used to lift PEOPLE. Don\'t use the machine if the date is overdue. Sticker is usually inside the cab.' },
      ],
    }],
  },
};

/**
 * Flat list of all template items in display order, with stable string keys
 * for use in tick_list_runs.responses (jsonb).
 *
 * Key format: `s${sectionIdx}_i${itemIdx}` — stable across re-orderings if you
 * keep the section/item indices.
 */
export function flattenItems(t: TickListTemplate): { key: string; text: string; desc?: string }[] {
  const out: { key: string; text: string; desc?: string }[] = [];
  t.sections.forEach((sec, si) => {
    sec.items.forEach((item, ii) => {
      out.push({ key: `s${si}_i${ii}`, text: item.text, desc: item.desc });
    });
  });
  return out;
}

// ---------- Daily-check variants ----------------------------------------------
// All three are stored under TickKind = 'daily' in the DB; only the rendered
// content differs. resolveDailyTemplate picks which content to show based on
// the site's project_type (set in the new-site form / setup wizard).

export const DAILY_SOLO: TickListTemplate = {
  kind: 'daily',
  title: 'End-of-day check',
  shortTitle: 'End of day',
  subtitle: '30 seconds. Before you knock off.',
  sections: [{
    items: [
      s('Work area tidy — swept up, offcuts in the bin'),
      s('Tools and tool boxes back in van or locked away'),
      s('Power isolated where you\'ve left exposed wiring or unfinished pipework'),
      s('Customer\'s home protected — dust sheets up, walkways clear, no damage'),
      s('Hot works gear (grinder, cut-off saw) cooled and checked for smouldering'),
      s('PPE in good nick — hi-vis, boots, gloves, glasses'),
      s('Power tools and leads checked — no cuts, guards on, plugs OK'),
      s('Anything you spotted that worried you — asbestos suspected, hidden services, structural concerns'),
      s('Any near-miss, slip or close call today'),
      s('Tomorrow\'s plan briefed (if continuing on site)'),
    ],
  }],
  tail: 'Tick what applies. Leave the rest.',
};

export const DAILY_SMALL: TickListTemplate = {
  kind: 'daily',
  title: 'Daily site check',
  shortTitle: 'Daily check',
  subtitle: '1 minute. Once a day on site.',
  sections: [{
    items: [
      s('Work area tidy — yesterday\'s mess cleared, materials stacked safely'),
      s('Welfare in place — toilet, drinking water, hand-wash'),
      s('First aid kit in van or on site, in date'),
      s('Power tools and leads — no cuts, guards on, RCD working'),
      s('Customer\'s home protected — dust sheets up, paths clear'),
      s('Anyone on site I haven\'t signed in / inducted? Stop and sort it'),
      s('Today\'s RAMS available to whoever is doing the work'),
      s('Hot works (if any) — extinguisher within reach, fire watch after'),
      s('Excavations / openings (if any) — barriers up, edges stable'),
      s('Anything that needs flagging to the customer or main contractor'),
    ],
  }],
  tail: 'If you can\'t tick something, fix it before continuing.',
};

/**
 * Pick the right daily template content for a site based on its project type.
 * Returns a full TickListTemplate. Falls back to the PC-style daily when nothing is set.
 */
export function resolveDailyTemplate(
  projectType: 'quick_job' | 'small_refurb' | 'full_site' | null | undefined,
): TickListTemplate {
  switch (projectType) {
    case 'quick_job':    return DAILY_SOLO;
    case 'small_refurb': return DAILY_SMALL;
    case 'full_site':    return TICK_LISTS.daily;
    default:             return TICK_LISTS.daily;
  }
}
